import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api, getAuthToken } from '../utils/api';
import { API_ENDPOINTS } from '../config';
import { setUser as gaSetUser } from '../utils/analytics';

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  is_email_confirmed: boolean;
  is_admin: boolean;
  is_banned: boolean;
  weekly_digest: boolean;
  daily_digest: boolean;
  img_url: string | null;
}

interface UserContextType {
  userProfile: UserProfile | null;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUserProfile(null);
      setIsLoading(false);
      return;
    }

    try {
      const data = await api.get<UserProfile>(API_ENDPOINTS.me);
      setUserProfile(data);
      const uid = localStorage.getItem('user_uid');
      if (uid) gaSetUser(uid);
    } catch (error) {
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        fetchProfile();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleTokenSet = () => {
      fetchProfile();
    };
    window.addEventListener('auth-token-set', handleTokenSet);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-token-set', handleTokenSet);
    };
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    await fetchProfile();
  }, [fetchProfile]);

  const updateProfile = (updates: Partial<UserProfile>) => {
    if (userProfile) {
      setUserProfile({ ...userProfile, ...updates });
    }
  };

  return (
    <UserContext.Provider value={{ userProfile, isLoading, refreshProfile, updateProfile }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

